import { useEffect, useLayoutEffect, useRef, useState, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import BoutonArreter, { useArretAgent } from "@/components/ArretAgent";

const API_URL = process.env.REACT_APP_BACKEND_URL || "";

/* Sections du projet accessibles depuis le telephone. Les trois dernieres
   ouvrent les ecrans du cockpit : ils n'ont pas de version mobile dediee,
   mais restent utilisables et valent mieux que pas d'acces du tout. */
const SECTIONS = [
  { cle: "acces", libelle: "Secrets et acces", mobile: true },
  { cle: "cdc", libelle: "CDC et progression" },
  { cle: "git", libelle: "Git" },
  { cle: "files", libelle: "Fichiers" },
  { cle: "server", libelle: "Serveur" },
];

/* ------------------------------------------------------------------ *
 * Le clavier iOS ne redimensionne pas la fenêtre : il recouvre l'écran.
 * visualViewport donne la hauteur réellement visible, ce qui permet de
 * garder le champ de saisie collé au-dessus du clavier.
 * ------------------------------------------------------------------ */
function useVisibleViewport() {
  const [v, setV] = useState(() => ({
    hauteur: typeof window !== "undefined" ? window.innerHeight : 0,
    decalage: 0,
  }));
  useEffect(() => {
    const vv = window.visualViewport;
    if (!vv) return;
    const maj = () => setV({ hauteur: vv.height, decalage: vv.offsetTop });
    vv.addEventListener("resize", maj);
    vv.addEventListener("scroll", maj);
    maj();
    return () => {
      vv.removeEventListener("resize", maj);
      vv.removeEventListener("scroll", maj);
    };
  }, []);
  return v;
}

/* Tant que le fil est affiche, le document ne doit pas defiler : seul le
   conteneur interne le fait. Sinon iOS deplace la page sous le clavier. */
function useDocumentFige() {
  useEffect(() => {
    const corps = document.body;
    const avant = corps.style.cssText;
    corps.style.overflow = "hidden";
    corps.style.position = "fixed";
    corps.style.width = "100%";
    corps.style.top = "0";
    return () => {
      corps.style.cssText = avant;
    };
  }, []);
}

/* Autoscroll qui rend la main : dès que l'utilisateur remonte lire,
   on cesse de le ramener en bas jusqu'à ce qu'il y revienne. */
function useStickyScroll(dep) {
  const ref = useRef(null);
  const colle = useRef(true);
  const onScroll = useCallback(() => {
    const el = ref.current;
    if (!el) return;
    colle.current = el.scrollHeight - el.scrollTop - el.clientHeight < 80;
  }, []);
  useLayoutEffect(() => {
    const el = ref.current;
    if (el && colle.current) el.scrollTop = el.scrollHeight;
  }, [dep]);
  return { ref, onScroll };
}

export default function MobileChatPage() {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const token = localStorage.getItem("odo_studio_token");

  const [projet, setProjet] = useState(null);
  const [messages, setMessages] = useState([]);
  const [sessionId, setSessionId] = useState(null);
  const [prompt, setPrompt] = useState("");
  const [enCours, setEnCours] = useState(false);
  const arret = useArretAgent(token, `/api/projects/${projectId}/claude/stop`);
  const [etape, setEtape] = useState("");
  const [historique, setHistorique] = useState([]);
  const [panneau, setPanneau] = useState(false);

  const vue = useVisibleViewport();
  useDocumentFige();
  const { ref: fil, onScroll } = useStickyScroll(messages);
  const zone = useRef(null);

  useEffect(() => {
    (async () => {
      const entetes = { Authorization: `Bearer ${token}` };
      const p = await fetch(`${API_URL}/api/projects/${projectId}`, { headers: entetes });
      if (p.ok) setProjet(await p.json());
      const c = await fetch(`${API_URL}/api/projects/${projectId}/conversations`, { headers: entetes });
      if (c.ok) {
        const convs = await c.json();
        setHistorique(convs);
        if (convs.length) {
          const derniere = convs[0];
          setSessionId(derniere.session_id);
          const d = await fetch(
            `${API_URL}/api/projects/${projectId}/conversations/${derniere.session_id}`,
            { headers: entetes }
          );
          if (d.ok) {
            const conv = await d.json();
            setMessages(conv.messages || []);
          }
        }
      }
    })();
  }, [projectId, token]);

  const nouvelleConversation = () => {
    setSessionId(null);
    setMessages([]);
    setPanneau(false);
  };

  const ouvrirConversation = async (conv) => {
    setPanneau(false);
    setSessionId(conv.session_id);
    setMessages([]);
    const r = await fetch(
      `${API_URL}/api/projects/${projectId}/conversations/${conv.session_id}`,
      { headers: { Authorization: `Bearer ${token}` } }
    );
    if (r.ok) {
      const d = await r.json();
      setMessages(d.messages || []);
    }
  };

  /* Le champ grandit avec le texte, jusqu'à 5 lignes. */
  const ajusteHauteur = (el) => {
    if (!el) return;
    el.style.height = "auto";
    el.style.height = Math.min(el.scrollHeight, 132) + "px";
  };

  const envoyer = async () => {
    const texte = prompt.trim();
    if (!texte || enCours) return;
    setPrompt("");
    if (zone.current) zone.current.style.height = "auto";
    setMessages((m) => [...m, { role: "user", text: texte }, { role: "assistant", text: "" }]);
    setEnCours(true);
    setEtape("Claude réfléchit…");

    try {
      const res = await fetch(`${API_URL}/api/projects/${projectId}/claude/run`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        ...(() => { const { signal, run_id } = arret.demarrer();
          return { body: JSON.stringify({ prompt: texte, resume: sessionId, run_id }), signal }; })(),
      });
      if (!res.ok) throw new Error("lancement impossible");

      const lecteur = res.body.getReader();
      const decodeur = new TextDecoder();
      let tampon = "";
      let reponse = "";

      while (true) {
        const { done, value } = await lecteur.read();
        if (done) break;
        tampon += decodeur.decode(value, { stream: true });
        const blocs = tampon.split("\n\n");
        tampon = blocs.pop();
        for (const bloc of blocs) {
          if (!bloc.startsWith("data: ")) continue;
          let evt;
          try {
            evt = JSON.parse(bloc.slice(6));
          } catch {
            continue;
          }
          if (evt.session_id) setSessionId(evt.session_id);

          if (evt.type === "assistant" && evt.message?.content) {
            const morceau = evt.message.content
              .filter((b) => b.type === "text")
              .map((b) => b.text)
              .join("");
            if (morceau) {
              reponse += morceau;
              setMessages((m) => {
                const copie = [...m];
                copie[copie.length - 1] = { role: "assistant", text: reponse };
                return copie;
              });
            }
            const outil = evt.message.content.find((b) => b.type === "tool_use");
            if (outil) setEtape(`${outil.name}…`);
          } else if (evt.type === "result" && evt.result) {
            reponse = evt.result;
            setMessages((m) => {
              const copie = [...m];
              copie[copie.length - 1] = { role: "assistant", text: reponse };
              return copie;
            });
          } else if (evt.type === "error") {
            setMessages((m) => {
              const copie = [...m];
              copie[copie.length - 1] = { role: "assistant", text: `⚠️ ${evt.message}` };
              return copie;
            });
          }
        }
      }
    } catch (e) {
      if (arret.estArret(e)) {
        setMessages((m) => {
          const copie = [...m];
          copie[copie.length - 1] = { role: "assistant", text: (copie[copie.length - 1]?.text || "") + "\n\n⏹ Arrêté à ta demande" };
          return copie;
        });
        return;
      }
      // Le backend persiste la reponse au fil de l'eau : on la recupere
      // au lieu de laisser l'utilisateur devant un message d'echec.
      setEtape("reprise…");
      let recuperee = false;
      for (let essai = 0; essai < 6 && !recuperee; essai++) {
        await new Promise((r) => setTimeout(r, 1500));
        try {
          const sid = sessionId;
          if (!sid) break;
          const r = await fetch(
            `${API_URL}/api/projects/${projectId}/conversations/${sid}`,
            { headers: { Authorization: `Bearer ${token}` } }
          );
          if (!r.ok) continue;
          const conv = await r.json();
          const derniers = conv.messages || [];
          const dernier = derniers[derniers.length - 1];
          if (dernier && dernier.role === "assistant" && dernier.text) {
            setMessages(derniers);
            recuperee = true;
          }
        } catch {
          /* on retente */
        }
      }
      if (!recuperee) {
        setMessages((m) => {
          const copie = [...m];
          copie[copie.length - 1] = {
            role: "assistant",
            text: "⚠️ Connexion interrompue et réponse non retrouvée. Rouvrez la conversation dans un instant : Claude continue de travailler côté serveur.",
          };
          return copie;
        });
      }
    } finally {
      setEnCours(false);
      setEtape("");
    }
  };

  return (
    <div
      className="flex flex-col overflow-hidden bg-[#faf9f7] text-[#1a1a1a]"
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        height: vue.hauteur ? `${vue.hauteur}px` : "100dvh",
        // annule le defilement qu'iOS applique au document a l'ouverture du clavier
        transform: `translateY(${vue.decalage}px)`,
      }}
    >
      {/* En-tête, sous l'encoche */}
      <header
        className="flex items-center gap-3 border-b border-black/5 bg-[#faf9f7]/90 px-4 backdrop-blur"
        style={{ paddingTop: "calc(max(env(safe-area-inset-top), 44px) + 8px)", paddingBottom: "10px" }}
      >
        <button
          onClick={() => navigate("/m")}
          className="-ml-2 flex h-9 w-9 items-center justify-center rounded-full active:bg-black/5"
          style={{ touchAction: "manipulation" }}
          aria-label="Retour"
        >
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M15 18l-6-6 6-6" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </button>
        <button
          onClick={() => setPanneau(true)}
          className="min-w-0 flex-1 text-left"
          style={{ touchAction: "manipulation" }}
        >
          <div className="truncate text-[15px] font-semibold">{projet?.name || "Projet"}</div>
          <div className="truncate text-[12px] text-black/45">
            {enCours ? etape || "en cours…" : sessionId ? "session reprise" : "nouvelle session"}
          </div>
        </button>
        <button
          onClick={() => navigate(`/m/projet/${projectId}/acces`)}
          className="flex h-9 w-9 items-center justify-center rounded-full active:bg-black/5"
          style={{ touchAction: "manipulation" }}
          aria-label="Acces et secrets"
        >
          <svg width="19" height="19" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <rect x="3" y="11" width="18" height="11" rx="2" />
            <path d="M7 11V7a5 5 0 0110 0v4" />
          </svg>
        </button>
        <button
          onClick={nouvelleConversation}
          className="-mr-1 flex h-9 w-9 items-center justify-center rounded-full active:bg-black/5"
          style={{ touchAction: "manipulation" }}
          aria-label="Nouvelle conversation"
        >
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M12 5v14M5 12h14" strokeLinecap="round" />
          </svg>
        </button>
      </header>

      {/* Historique : feuille qui glisse depuis le bas */}
      {panneau && (
        <>
          <div
            className="fixed inset-0 z-40 bg-black/25"
            onClick={() => setPanneau(false)}
          />
          <div
            className="fixed inset-x-0 bottom-0 z-50 max-h-[70dvh] overflow-y-auto rounded-t-3xl bg-white px-4 pt-2"
            style={{
              paddingBottom: "calc(env(safe-area-inset-bottom) + 16px)",
              WebkitOverflowScrolling: "touch",
            }}
          >
            <div className="mx-auto mb-3 mt-1 h-1 w-9 rounded-full bg-black/15" />
            <div className="px-1 pb-2 text-[13px] font-medium uppercase tracking-wide text-black/40">
              Sections
            </div>
            <div className="mb-4 grid grid-cols-2 gap-2">
              {SECTIONS.map((s) => (
                <button
                  key={s.cle}
                  onClick={() => {
                    setPanneau(false);
                    navigate(
                      s.mobile
                        ? `/m/projet/${projectId}/${s.cle}`
                        : `/dashboard/projects/${projectId}/${s.cle}`
                    );
                  }}
                  className="rounded-2xl bg-black/[0.04] px-3 py-3 text-left text-[14px] active:bg-black/10"
                  style={{ touchAction: "manipulation" }}
                >
                  {s.libelle}
                </button>
              ))}
            </div>

            <div className="px-1 pb-2 text-[13px] font-medium uppercase tracking-wide text-black/40">
              Conversations
            </div>
            {historique.length === 0 && (
              <div className="px-1 pb-6 pt-2 text-[15px] text-black/40">
                Aucune conversation pour ce projet.
              </div>
            )}
            {historique.map((c) => (
              <button
                key={c.session_id}
                onClick={() => ouvrirConversation(c)}
                className={`mb-1 block w-full rounded-2xl px-3 py-3 text-left active:bg-black/5 ${
                  c.session_id === sessionId ? "bg-black/[0.04]" : ""
                }`}
                style={{ touchAction: "manipulation" }}
              >
                <div className="truncate text-[15px] leading-snug">{c.title || "Conversation"}</div>
                <div className="mt-0.5 text-[12px] text-black/40">
                  {(c.messages?.length ?? 0)} message(s) ·{" "}
                  {(c.updated_at || c.created_at || "").slice(0, 10).split("-").reverse().join("/")}
                </div>
              </button>
            ))}
          </div>
        </>
      )}

      {/* Fil de conversation */}
      <div
        ref={fil}
        onScroll={onScroll}
        className="flex-1 overflow-y-auto overscroll-contain px-4 py-4"
        style={{ WebkitOverflowScrolling: "touch" }}
      >
        {messages.length === 0 && (
          <div className="mt-24 text-center text-[15px] leading-relaxed text-black/40">
            Posez votre question.<br />La conversation reprend là où elle s'était arrêtée.
          </div>
        )}
        {messages.map((m, i) => (
          <div key={i} className={`mb-3 flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
            <div
              className={
                m.role === "user"
                  ? "max-w-[85%] rounded-3xl rounded-br-lg bg-[#1a1a1a] px-4 py-2.5 text-[16px] leading-[1.5] text-white"
                  : "max-w-[92%] whitespace-pre-wrap text-[16px] leading-[1.6] text-[#1a1a1a]"
              }
            >
              {m.text || (enCours && i === messages.length - 1 ? <Points /> : null)}
            </div>
          </div>
        ))}
      </div>

      {/* Composeur, au-dessus du clavier et de la barre de gestes */}
      <div
        className="border-t border-black/5 bg-[#faf9f7] px-3 pt-2"
        style={{ paddingBottom: "calc(env(safe-area-inset-bottom) + 8px)" }}
      >
        <div className="flex items-end gap-2 rounded-[22px] border border-black/10 bg-white px-3 py-2">
          <textarea
            ref={zone}
            rows={1}
            value={prompt}
            onChange={(e) => {
              setPrompt(e.target.value);
              ajusteHauteur(e.target);
            }}
            placeholder="Votre message"
            enterKeyHint="send"
            className="max-h-[132px] flex-1 resize-none bg-transparent leading-[1.45] outline-none placeholder:text-black/30"
            style={{ fontSize: "16px" }} /* en dessous, iOS zoome tout seul */
          />
          {enCours ? <BoutonArreter compact onClick={arret.arreter} className="mb-0.5" /> : <button
            onClick={envoyer}
            disabled={!prompt.trim() || enCours}
            className="mb-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-[#1a1a1a] text-white transition-opacity disabled:opacity-25"
            style={{ touchAction: "manipulation" }}
            aria-label="Envoyer"
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
              <path d="M12 19V5M5 12l7-7 7 7" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </button>}
        </div>
      </div>
    </div>
  );
}

function Points() {
  return (
    <span className="inline-flex gap-1 py-2" aria-label="en cours">
      {[0, 1, 2].map((i) => (
        <span
          key={i}
          className="h-1.5 w-1.5 rounded-full bg-black/25"
          style={{ animation: `nfPulse 1.2s ${i * 0.16}s infinite ease-in-out` }}
        />
      ))}
      <style>{`@keyframes nfPulse{0%,60%,100%{opacity:.25;transform:translateY(0)}30%{opacity:.7;transform:translateY(-2px)}}`}</style>
    </span>
  );
}
